import { send } from "@utils/fetch";
import store from "@utils/redux/store";
import { captureException } from "@utils/sentry";
import ExperimentTracker from "./ExperimentTracker";

const REFERRER_TO_SEARCH_ENGINE_MAPPING = {
  ".*duckduckgo\\..*": "duckduckgo",
  ".*yahoo\\..*": "yahoo",
  ".*yandex\\..*": "yandex",
  ".*baidu\\..*": "baidu",
  ".*naver\\..*": "naver",
  ".*google\\..*": "google",
  ".*bing\\..*": "bing",
};
const REFERRER_IGNORE_REGEX = ".*(app|email|gclid|paid).*";
const REFERRER_MATCH = [
  "^http://localhost:8080",
  "^https://web.noom.com",
  "^https://try.noom.com",
  "^https://ww(w|1).noom.com(/)?$",
  "^https://ww(w|1).noom.com/(en|es|de|ja|jp)",
  "^https://ww(w|1).noom.com/programs/health-weight/",
  "^https://ww(w|1).noom.com/(en|es|de|ja|jp)/programs",
  "^https://ww(w|1).noom.com/(purchase|about|careers|news)",
  "^https://buyflow-web-assets.noom.com",
  "^https://buyflow-web-assets.test.wsli.dev",
  "^https://buyflow-lambda.(dev|test).wsli.dev",
]
  .map((domain) => `(${domain})`)
  .reduce((val, acc) => `${acc}|${val}`);

/**
 * Class for tracking events on the client. Currently tracks to Mixpanel and Google Analytics.
 * Google Analytics should be initialized already from with the html templates.
 *
 * Mixpanel is tracked on the server-side using the growth pixel. Relies on pulling in the
 * redux state to get the distinct_id. In the future, when cookie issues are resolved, server should be able to
 * correctly identify the user without the client having to pass the distinct id.
 *
 * Copyright (C) 2018 Noom, Inc.
 * @author sumin
 */
export default class EventTracker {
  constructor(userIsBot = false) {
    this.userIsBot = userIsBot;

    // Set initial properties so that we create a people profile on Mixpanel.
    this.setInitialProperties();
  }

  setInitialProperties() {
    const { userData, geoLocation, language } = store.getState();
    const properties = {
      mixpanelDistinctId: userData.mixpanel_distinct_id,
      country: geoLocation.country_code,
      subdivision: geoLocation.subdivision,
      language,
    };
    if (this.userIsBot) properties.userIsBot = true;
    this.updateUserContext(properties);
  }

  trackOnMainSurvey(questionId) {
    this.trackGenericEvent("OnMainSurvey", { questionId });
  }

  trackSurveyAnswers(questionId, answers) {
    const questionIdCapitalized =
      questionId.charAt(0).toUpperCase() + questionId.slice(1);

    this.trackGenericEvent(`AnsweredQuestion${questionIdCapitalized}`, answers);
    this.updateUserContext(answers);
  }

  trackOnHMMainSurvey(questionId) {
    this.trackGenericEvent("OnHMMainSurvey", { questionId });
  }

  trackOnPaymentSurvey(questionId) {
    this.trackGenericEvent("OnPaymentSurvey", { questionId });
  }

  trackOnHMPaymentSurvey(questionId) {
    this.trackGenericEvent("OnHMPaymentSurvey", { questionId });
  }

  trackGotEmail(email, name) {
    // TODO(sumin): Remove this since we add email and name to people properties on the server-side.
    return this.trackGenericEvent("GotEmail", {
      EnteredEmail: email,
    });
  }

  trackGenericEvent(eventName, properties = {}) {
    console.log("EventTracker", "trackGenericEvent", eventName, properties);

    return EventTracker.pixelTrackEvent(eventName, properties);
  }

  updateUserContext(properties) {
    console.log("EventTracker", "updateUserContext", properties);
    EventTracker.pixelPeopleSet(properties);
  }

  /**
   * Set people properties using the growth pixel.
   */
  static pixelPeopleSet(properties) {
    const { userData } = store.getState();
    const people = JSON.stringify(properties);
    if (people === "{}") return;
    const requestBody = {
      mixpanel_distinct_id: userData.mixpanel_distinct_id,
      people,
    };
    return EventTracker.sendPixelRequest(requestBody, "people_set");
  }

  /**
   * Track events using the growth pixel.
   */
  static pixelTrackEvent(name, eventProperties) {
    const { URL, referrer } = document;
    const { userData, geoLocation, language } = store.getState();
    const requestBody = {
      mixpanel_distinct_id: userData.mixpanel_distinct_id,
      currentUrl: URL,
      eventName: name,
      eventProperties,
    };

    // Prepare event properties.
    requestBody.eventProperties = {
      country: geoLocation.country_code,
      subdivision: geoLocation.subdivision,
      language,
      ...eventProperties,
      ...EventTracker.collectClientProperties(),
      ...EventTracker.collectExperimentProperties(),
      ...EventTracker.collectVisitProperties(),
      ...EventTracker.collectSEOProperties(),
    };

    // Maybe add the new_session property to mixpanel.
    // TODO(sumin): See if we can get rid of this.
    if (
      window.active_session !== true &&
      referrer.match(REFERRER_MATCH) == null &&
      URL.match(`app-[0-9]*-day`) == null
    ) {
      requestBody.eventProperties.new_session = true;
      window.active_session = true;
    }

    return EventTracker.sendPixelRequest(requestBody, "track", "v3");
  }

  static sendPixelRequest(requestBody, requestType, version = "v2") {
    // TODO: track usage
    return send(
      "POST",
      `/pixel/${version}/i/${requestType}/`,
      requestBody
    ).catch((e) => {
      captureException(e, "EventTracker-Failed to fetch");
    });
  }

  /**
   * Collect client specific properties.
   */
  static collectClientProperties() {
    const properties = {
      $screen_width: window.screen.width,
      $screen_height: window.screen.height,
    };
    Object.assign(properties, EventTracker.getFullStoryProperties());
    return properties;
  }

  static collectExperimentProperties() {
    // TODO(sumin): Maybe move this logic into ExperimentTracker.
    const [
      optimizelyProperties,
      hasNewOptimizelyProperties,
    ] = window.experimentTracker.getOptimizelyProperties();

    // If there are new optimizely properties make sure to update the visit.
    if (hasNewOptimizelyProperties && window.visitTracker) {
      window.visitTracker.updateRawData({
        optimizely_properties: optimizelyProperties,
      });
    }

    return {
      ...optimizelyProperties,
      ...ExperimentTracker.getMeristemExperimentState(),
    };
  }

  static collectVisitProperties() {
    if (window.visitTracker) {
      return EventTracker.prepareMixpanelProperties(
        window.visitTracker.getUrlParams()
      );
    }
    return {};
  }

  /**
   * Collect SEO properties(search engine extracted from document.referrer)
   * We also should return empty dict if referrer contains certain words
   */
  static collectSEOProperties() {
    try {
      const { referrer } = document;
      let searchEngine = null;
      if (
        referrer &&
        referrer.length > 0 &&
        !referrer.match(REFERRER_IGNORE_REGEX)
      ) {
        searchEngine =
          REFERRER_TO_SEARCH_ENGINE_MAPPING[
            Object.keys(REFERRER_TO_SEARCH_ENGINE_MAPPING).find((key) =>
              referrer.match(key)
            )
          ];
      }
      if (searchEngine) return { searchEngine };
      return {};
    } catch {
      return {};
    }
  }

  static prepareMixpanelProperties(urlParams) {
    const utmParamsMapping = {
      utm_source: "UTM Source - Current",
      utm_medium: "UTM Medium - Current",
      utm_campaign: "UTM Campaign - Current",
      utm_content: "UTM Content - Current",
      utm_term: "UTM Term - Current",
      referrer: "Referrer URL",
    };
    const mixpanelProperties = {};
    Object.keys(urlParams).forEach((paramKey) => {
      if (paramKey in utmParamsMapping) {
        mixpanelProperties[utmParamsMapping[paramKey]] = urlParams[paramKey];
      } else {
        mixpanelProperties[paramKey] = urlParams[paramKey];
      }
    });
    return mixpanelProperties;
  }

  /**
   * Returns the FullStory session url.
   */
  static getFullStoryProperties() {
    try {
      return { "FullStory Session": window.FS.getCurrentSessionURL() };
    } catch (e) {
      return { fsError: e };
    }
  }

  static getUserAttributes(url, referrer) {
    // Get all parameters from current url.
    const params = {};
    const windowURL = new URL(url);
    windowURL.searchParams.forEach((value, key) => {
      params[key] = value;
    });

    // Always explicitly set the referrer param since we don't want the pixel to use the HTTP_REFERER of the request.
    params.referrer = referrer;
    if (referrer == null || referrer.length === 0) {
      params.referrer = "none";
    }
    return params;
  }
}
